/**
 * EditForkDialog — the ask-on-edit prompt for a set that campaigns rely on:
 * update it for everyone, or fork a copy (optionally repointing the loaded
 * package so only THIS context moves to the new version).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { GitBranch, Users } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import ModalCard from '@/components/ui/layout/ModalCard';

import ActionRow from './ActionRow';

export interface EditForkDialogProps {
  open: boolean;
  setName: string;
  packages: number;
  campaigns: number;
  /** True when a loaded package will be repointed at the copy. */
  willRepoint: boolean;
  busy: boolean;
  onUpdateAll: () => void;
  onSaveCopy: () => void;
  onCancel: () => void;
}

const EditForkDialog: FC<EditForkDialogProps> = ({
  open,
  setName,
  packages,
  campaigns,
  willRepoint,
  busy,
  onUpdateAll,
  onSaveCopy,
  onCancel,
}) => (
  <ModalCard
    open={open}
    title="This set is in use"
    subtitle={`"${setName}" is used by ${packages} package(s) across ${campaigns} campaign(s). Choose how to save your changes.`}
    widthClass="max-w-md"
    zClass="z-[160]"
    busy={busy}
    onClose={onCancel}
  >
    <div className="space-y-2">
      <ActionRow
        icon={<Users className="h-4 w-4" aria-hidden />}
        title={`Update for everyone (${packages} packages · ${campaigns} campaigns)`}
        caption="Those campaigns load the new version from now on. Already-applied configs are not rewritten until their next apply."
        primary
        disabled={busy}
        onClick={onUpdateAll}
      />
      <ActionRow
        icon={<GitBranch className="h-4 w-4" aria-hidden />}
        title="Save as copy"
        caption={
          willRepoint
            ? 'Creates a copy and points the loaded package at it. Other packages keep the original.'
            : `Creates "${setName} (copy)" — nothing else changes.`
        }
        disabled={busy}
        onClick={onSaveCopy}
      />
      <div className="flex justify-end pt-1">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  </ModalCard>
);

export default EditForkDialog;
